import { computed, makeObservable } from 'mobx';

import { concatPath } from '@feathr/hooks';
import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { Collection, DisplayModel } from '@feathr/rachis';

export enum APIKeyServiceNames {
  Zapier = 'Zapier',
  FeathrAPI = 'Feathr API',
}

/**
 * Distinguishes the original JWT-based API keys (`legacy`) from the V2 scoped
 * "service" keys. The backend sets this on every serialized key.
 */
export type TAPIKeyKind = 'legacy' | 'service';

/**
 * The selectable scope vocabulary for V2 service keys. Mirrors the create-path
 * vocabulary the backend validates against (`anhinga/auth/scopes.py`): the
 * read/write matrix for every scoped resource plus the read-only `feed:read`.
 *
 * The `*` wildcard is a grant level (held by grandfathered keys), not a
 * requestable permission, so it is intentionally excluded from this picker set.
 */
export const API_KEY_SCOPES = [
  'campaigns:read',
  'campaigns:write',
  'projects:read',
  'projects:write',
  'persons:read',
  'persons:write',
  'breadcrumbs:read',
  'breadcrumbs:write',
  'tags:read',
  'tags:write',
  'custom_fields:read',
  'custom_fields:write',
  'feed:read',
] as const;

export type TAPIKeyScope = (typeof API_KEY_SCOPES)[number];

export interface IAPIKey extends IBaseAttributes {
  type: 'APIKey';
  /** Whether this is an original JWT key (`legacy`) or a V2 scoped key (`service`). */
  kind: TAPIKeyKind;
  service_name: APIKeyServiceNames;
  /**
   *  Expected request host using the API key. set in the backend
   *  based on name. Present on legacy keys only, so optional here.
   */
  host?: string;
  /** The JWT key string. Present on legacy keys only, so optional here. */
  key?: string;
  /** Id of the parent class. Most likely the Account_Id */
  parent: string;
  /** Name of the parent class. Most Likely Account */
  parent_kind: string;
  /** True when the key is revoked */
  revoked: boolean;
  /**
   * The full, plaintext service-key value. Returned exactly once, on the create
   * response; never present on a stored or listed record. Service keys only, so
   * optional — consumers must guard before reading it.
   */
  secret?: string;
  /** The non-secret prefix of a service key, safe to display. Service keys only. */
  prefix?: string;
  /** The last four characters of a service key, safe to display. Service keys only. */
  last_four?: string;
  /** The scopes granted to a service key. Service keys only. */
  scopes?: TAPIKeyScope[];
  /**
   * ISO 8601 expiration. Sent on create; the backend stores it but does not yet
   * return it, so consumers must not assume it is present on a fetched record.
   */
  expires_at?: string;
  /** ISO 8601 timestamp of the key's last use. Service keys only. */
  last_used_at?: string;
  /** Stringified id of the user who created the key. Service keys only. */
  created_by?: string;
}

export class APIKey extends DisplayModel<IAPIKey> {
  public readonly className = 'APIKey';

  public get constraints(): TConstraints<IAPIKey> {
    return {
      service_name: {
        presence: {
          message: '^Service name cannot be empty.',
          allowEmpty: false,
        },
      },
    };
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`/settings/integrations/api-keys/${this.id}`, pathSuffix);
  }

  constructor(attributes: Partial<IAPIKey> = {}) {
    super(attributes);

    makeObservable(this);
  }

  @computed
  public get name(): string {
    return (this.get('service_name') ?? '').trim() || 'Unnamed API Key';
  }
}

export class APIKeys extends Collection<APIKey> {
  public getClassName(): string {
    return 'api_keys';
  }

  public getModel(attributes: Partial<IAPIKey>): APIKey {
    return new APIKey(attributes);
  }
}
